import { startTransition, useCallback } from 'react';


import { fg } from '@atlaskit/platform-feature-flags';

import {
	onTaskAbort,
	onTaskFail,
	onTaskStart,
	onTaskSuccess,
	TASK_NAMES,
} from '../../common/utils/events/vital-stats-events';
import { useAnalytics } from '../analytics';

import { useQuickFindAttributes } from './analytics';
import { useVitalStatsTaskState } from './quickfind-post-query-vitalstats-context';

type AbortReason =
	| 'dialog_closed_during_network'
	| 'superseded_by_new_query'
	| 'dialog_closed_before_debounce_fired'
	| 'text_cleared';

/**
 * Hook to manage VitalStats events for quickfind post-query UX task lifecycle.
 * Tracks task state and provides methods to fire appropriate VitalStats events.
 * Uses shared context to maintain state across multiple hook instances.
 */
export const useQuickFindPostQueryVitalStats = () => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes } = useQuickFindAttributes();
	const taskStateRef = useVitalStatsTaskState();

	const isEnabled = fg('rovo-search-vitalstats-quickfind-postquery-ux');

	const startTask = useCallback(() => {
		if (!isEnabled) {
			return;
		}

		const taskId = crypto.randomUUID();
		const searchSessionId = commonAttributes.searchSessionId || '';

		// Set the shared context state
		if (taskStateRef.current) {
			taskStateRef.current.id = taskId;
			taskStateRef.current.started = true;
			taskStateRef.current.completed = false;
		}

		fireAnalyticsEvent(
			onTaskStart({
				searchSessionId,
				taskId,
				task: TASK_NAMES.QUICK_FIND_POST_QUERY_UX,
			}),
		);
	}, [isEnabled, commonAttributes.searchSessionId, fireAnalyticsEvent, taskStateRef]);

	const completeTaskSuccess = useCallback(() => {
		// Check if task is active using the shared context state
		if (!isEnabled || !taskStateRef.current?.started || taskStateRef.current?.completed) {
			return;
		}

		const { id: taskId } = taskStateRef.current;
		const searchSessionId = commonAttributes.searchSessionId || '';

		// taskId is guaranteed to be non-null when started is true
		if (!taskId) {
			return;
		}

		fireAnalyticsEvent(
			onTaskSuccess({
				searchSessionId,
				taskId,
				task: TASK_NAMES.QUICK_FIND_POST_QUERY_UX,
			}),
		);

		// Mark task as completed
		if (taskStateRef.current) {
			taskStateRef.current.completed = true;
		}
	}, [isEnabled, commonAttributes.searchSessionId, fireAnalyticsEvent, taskStateRef]);

	const completeTaskFail = useCallback(
		(error?: Error) => {
			// Check if task is active using the shared context state
			if (!isEnabled || !taskStateRef.current?.started || taskStateRef.current?.completed) {
				return;
			}

			const { id: taskId } = taskStateRef.current;
			const searchSessionId = commonAttributes.searchSessionId || '';

			// taskId is guaranteed to be non-null when started is true
			if (!taskId) {
				return;
			}

			fireAnalyticsEvent(
				onTaskFail({
					searchSessionId,
					taskId,
					task: TASK_NAMES.QUICK_FIND_POST_QUERY_UX,
					moreContext: {
						errorName: error?.name,
						errorMessage: error?.message,
					},
				}),
			);

			// Mark task as completed
			taskStateRef.current.completed = true;
		},
		[isEnabled, commonAttributes.searchSessionId, fireAnalyticsEvent, taskStateRef],
	);

	const abortTask = useCallback(
		(abortReason: AbortReason) => {
			// Check if task is active using the shared context state
			if (!isEnabled || !taskStateRef.current?.started || taskStateRef.current?.completed) {
				return;
			}

			const { id: taskId } = taskStateRef.current;
			const searchSessionId = commonAttributes.searchSessionId || '';

			// taskId is guaranteed to be non-null when started is true
			if (!taskId) {
				return;
			}

			// Mark task as completed FIRST (synchronously) to prevent race conditions
			taskStateRef.current.completed = true;

			// Then defer the analytics event to not block rendering
			startTransition(() => {
				fireAnalyticsEvent(
					onTaskAbort({
						searchSessionId,
						taskId,
						task: TASK_NAMES.QUICK_FIND_POST_QUERY_UX,
						moreContext: {
							abortReason,
						},
					}),
				);
			});
		},
		[isEnabled, commonAttributes.searchSessionId, fireAnalyticsEvent, taskStateRef],
	);

	const isTaskActive = useCallback(() => {
		// Check if task has started but not yet completed
		return taskStateRef.current?.started && !taskStateRef.current?.completed;
	}, [taskStateRef]);

	return {
		startTask,
		completeTaskSuccess,
		completeTaskFail,
		abortTask,
		isTaskActive,
	};
};
